import type { BcfTopic } from './bcfParser'
import { baseDaOrigem } from './bcfOrigem'
import {
  type ConflitoDeCampo,
  type Fotografia,
  juntarComentarios,
  mudouDesdeABase,
  reconciliar,
} from './bcfSync'
import {
  type ComentarioNoFio,
  type Envio,
  type TopicoNoFio,
  apagadosEm,
  apagadosOrfaos,
  comentariosOrfaos,
  compor,
  emLotes,
  paraEnvio,
} from './bcfSyncProtocolo'

/** A sincronização, do princípio ao fim.
 *
 *  Junta as três peças que já existem: o que o servidor diz que mudou,
 *  o que está aqui, e a fotografia de quando os dois lados estavam de
 *  acordo. Chama o motor de reconciliação, aplica o que não tem dúvida,
 *  envia o que é nosso, e devolve os conflitos para alguém decidir.
 *
 *  As entradas e saídas passam por uma `Porta`, que é o que torna isto
 *  testável sem rede e sem base de dados. O que corre aqui são as
 *  decisões de ordem, e a ordem importa:
 *
 *  1. **Ler o servidor antes de enviar.** Ao contrário, enviava-se por
 *     cima de alterações que ainda não tínhamos visto, e o conflito só
 *     aparecia na sincronização seguinte, depois de o estrago estar
 *     feito.
 *  2. **Enviar só o que mudou cá, e dizer até onde se leu.** O servidor
 *     recusa um tópico que outra pessoa tenha alterado depois da nossa
 *     leitura, em vez de o escrever por cima.
 *  3. **Gravar a base só no fim.** A base é a memória de onde os dois
 *     lados estavam de acordo. O que ficou em conflito ou foi recusado
 *     mantém a entrada anterior: continua por acordar, e não se pode
 *     esquecer de onde se partiu.
 *  4. **A base guarda a posição da leitura, não a do envio.** O que
 *     outra pessoa enviou entre a nossa leitura e o nosso envio tem de
 *     vir na próxima vez.
 *  5. **Um conflito não trava o resto.** O que não tem dúvida aplica-se
 *     e envia-se na mesma.
 *  6. **A base é o que ficou acordado, lido logo a seguir a aplicar.**
 *     Com a sincronização ao vivo, quem está a escrever continua a
 *     escrever enquanto o envio está a caminho. Ler o local para a base
 *     depois do envio guardava essas teclas como acordadas sem nunca as
 *     ter enviado, e a sincronização seguinte já não as via.
 *  7. **Sem base, a origem no ficheiro.** Um tópico que os dois lados
 *     importaram do mesmo ficheiro tem uma base comum mesmo que nunca
 *     se tenham sincronizado (ver `bcfOrigem.ts`).
 */

export interface RespostaDoServidor {
  seq: number
  topicos: TopicoNoFio[]
  comentarios: ComentarioNoFio[]
  bloqueios: { topic_guid: string; nome: string | null; meu: boolean }[]
}

export interface Recusa {
  guid: string
  motivo: string
  /** 'bloqueio': outra pessoa está a editar o tópico. 'versao': o tópico
   *  mudou no servidor depois da nossa leitura. */
  tipo?: 'bloqueio' | 'versao'
}

export interface RespostaDoEnvio {
  seq: number
  aceites: number
  recusas: Recusa[]
}

export interface Base {
  seq: number
  topicos: BcfTopic[]
  /** Conflitos por resolver. Enquanto estiverem aqui, o tópico não se
   *  envia: enviá-lo escrevia o valor de cá por cima do de lá sem
   *  ninguém ter escolhido. */
  pendentes?: ConflitoDeCampo[]
}

/** A ligação a UM BCF de um workspace: o servidor guarda cada BCF em
 *  separado, e a porta já sabe qual é (ver `portaDoBcf`). */
export interface Porta {
  /** O que mudou lá desde o número dado. */
  lerRemoto: (desde: number) => Promise<RespostaDoServidor>
  /** Entrega um lote. `lido` é o número até onde se leu o servidor. */
  enviar: (envio: Envio, lido: number) => Promise<RespostaDoEnvio>
  /** A fotografia do último acordo, ou null se nunca houve. */
  lerBase: () => Promise<Base | null>
  gravarBase: (b: Base) => Promise<void>
  /** O que está aqui agora. */
  lerLocal: () => BcfTopic[]
  /** Escreve o que veio de fora. */
  aplicarLocal: (topicos: BcfTopic[], apagados: string[]) => void
}

export interface Resultado {
  recebidos: number
  enviados: number
  comentariosNovos: number
  conflitos: ConflitoDeCampo[]
  /** Tópicos que o servidor recusou: outra pessoa tem-nos, ou mudaram
   *  depois da nossa leitura. */
  recusados: Recusa[]
  /** Quem está a editar o quê, agora. */
  bloqueios: { topic_guid: string; nome: string | null; meu: boolean }[]
  seq: number
}

/** A base serve a ESTE BCF? Uma base com tópicos de que nenhum está cá
 *  é de um BCF local que já não existe (a sessão fechou, ou o BCF foi
 *  removido e aberto outra vez da partilha): o número de leitura dela
 *  dizia "já li até aqui" e o BCF novo, vazio, ficava sem os tópicos que
 *  o servidor tem. Nesse caso lê-se tudo, como da primeira vez. */
export function baseServe(base: Base, locais: readonly BcfTopic[]): boolean {
  if (base.topicos.length === 0) return true
  const ca = new Set(locais.map((t) => t.guid))
  return base.topicos.some((t) => ca.has(t.guid))
}

export async function sincronizar(porta: Porta): Promise<Resultado> {
  const guardada = await porta.lerBase()
  const base: Base =
    guardada && baseServe(guardada, porta.lerLocal()) ? guardada : { seq: 0, topicos: [] }

  // 1. Ler antes de enviar.
  const remoto = await porta.lerRemoto(base.seq)

  const vindos = compor(remoto.topicos, remoto.comentarios)
  const apagados = apagadosEm(remoto.topicos)
  const locais = porta.lerLocal()
  const bases = new Map(base.topicos.map((t) => [t.guid, t]))

  // Comentários (e comentários apagados) cujo tópico não veio porque não
  // mudou lá. O que o servidor tem do tópico é o que está na base; só
  // sem base se usa o de cá. Montar a partir do de cá fazia parecer que
  // o servidor já tinha as nossas alterações aos campos, e elas nunca
  // eram enviadas.
  const orfaos = comentariosOrfaos(remoto.topicos, remoto.comentarios)
  const lapides = apagadosOrfaos(remoto.topicos, remoto.comentarios)
  const remotoCompleto = [...vindos]
  for (const topicGuid of new Set([...orfaos.keys(), ...lapides.keys()])) {
    const meu = locais.find((t) => t.guid === topicGuid)
    if (!meu) continue
    const la = bases.get(topicGuid) ?? meu
    const apagadosLa = [
      ...new Set([...(la.commentsApagados ?? []), ...(lapides.get(topicGuid) ?? [])]),
    ]
    remotoCompleto.push({
      ...la,
      comments: juntarComentarios(la.comments, orfaos.get(topicGuid) ?? [], apagadosLa),
      ...(apagadosLa.length > 0 ? { commentsApagados: apagadosLa } : {}),
    })
  }

  const fotografiaLocal: Fotografia = { topicos: locais }
  const fotografiaRemota: Fotografia = { topicos: remotoCompleto }
  const r = reconciliar(base, fotografiaLocal, fotografiaRemota, {
    baseSemAcordo: baseDaOrigem,
  })

  // Conflitos de sincronizações anteriores que ainda ninguém resolveu:
  // voltam a aparecer, e o tópico não se envia enquanto lá estiverem.
  const pendentesAntes = (base.pendentes ?? []).filter(
    (p) => !r.conflitos.some((c) => c.guid === p.guid && c.campo === p.campo),
  )
  const conflitos = [...r.conflitos, ...pendentesAntes]
  const comConflito = new Set(conflitos.map((c) => c.guid))

  // 5. Um conflito não trava o resto.
  if (r.aplicarCa.length > 0 || apagados.length > 0) {
    porta.aplicarLocal(r.aplicarCa, apagados)
  }
  // 6. O que fica acordado é o que está cá agora, antes de qualquer
  // espera: entre ler o local e aplicar não houve nenhuma, por isso
  // isto é exactamente a reconciliação, sem o que alguém escreva
  // enquanto o envio vai a caminho.
  const depois = porta.lerLocal()

  // 2. Enviar só o que mudou cá, em lotes que o servidor aceita, e com a
  // posição da leitura para ele poder recusar o que mudou entretanto.
  // Um tópico em conflito não se envia, mas as respostas que lhe deram
  // cá seguem na mesma: a referência dele passa a ter os campos de cá,
  // e só os comentários diferem.
  const referencias = new Map(r.referenciaLa)
  for (const t of r.enviarLa) {
    if (!comConflito.has(t.guid)) continue
    const ref = r.referenciaLa.get(t.guid) ?? null
    referencias.set(t.guid, {
      ...t,
      comments: ref?.comments ?? [],
      commentsApagados: ref?.commentsApagados ?? [],
    })
  }
  const aEnviar = r.enviarLa.filter((t) => !comConflito.has(t.guid))
  const recusados: Recusa[] = []
  if (r.enviarLa.length > 0) {
    const envio = paraEnvio(r.enviarLa, referencias)
    for (const lote of emLotes(envio)) {
      const resposta = await porta.enviar(lote, remoto.seq)
      recusados.push(...resposta.recusas)
    }
  }
  const recusadosGuids = new Set(recusados.map((x) => x.guid))
  const enviados = aEnviar.filter((t) => !recusadosGuids.has(t.guid)).length

  // 3 e 4. A base no fim. O que ficou por acordar mantém a entrada
  // anterior. A posição é a da leitura, e não o que o envio devolve: o
  // máximo do servidor depois de gravarmos inclui o que outra pessoa
  // tenha gravado entre a leitura e o envio, e guardá-lo dava essas
  // alterações por recebidas sem nunca as termos lido.
  const porAcordar = new Set([...recusadosGuids, ...comConflito])
  const topicosDaBase: BcfTopic[] = []
  for (const t of depois) {
    if (!porAcordar.has(t.guid)) topicosDaBase.push(t)
    else {
      const anterior = bases.get(t.guid)
      if (anterior) topicosDaBase.push(anterior)
    }
  }
  await porta.gravarBase({
    seq: remoto.seq,
    topicos: topicosDaBase,
    pendentes: conflitos,
  })

  return {
    recebidos: r.aplicarCa.length,
    enviados,
    comentariosNovos: r.comentariosNovos,
    conflitos,
    recusados,
    bloqueios: remoto.bloqueios,
    seq: remoto.seq,
  }
}

/** Quantos tópicos têm alterações cá que o servidor ainda não tem: os
 *  que mudaram desde a base, e os que ainda não estão nela. Os que têm
 *  conflitos por resolver não contam: não se enviam até alguém escolher.
 *  É o número que a interface mostra como "alterações por enviar". */
export function contarPorEnviar(base: Base | null, locais: readonly BcfTopic[]): number {
  if (!base) return locais.length
  const bases = new Map(base.topicos.map((t) => [t.guid, t]))
  const pendentes = new Set((base.pendentes ?? []).map((p) => p.guid))
  let n = 0
  for (const t of locais) {
    if (pendentes.has(t.guid)) continue
    const b = bases.get(t.guid)
    if (!b || mudouDesdeABase(t, b)) n += 1
  }
  return n
}

/** Tira da base os conflitos já resolvidos, para o tópico voltar a ser
 *  enviado na sincronização seguinte. */
export function semPendentes(
  base: Base,
  guid: string,
  campos: readonly string[],
): Base {
  return {
    ...base,
    pendentes: (base.pendentes ?? []).filter(
      (p) => p.guid !== guid || !campos.includes(p.campo),
    ),
  }
}

/** O resumo, dito a quem carregou no botão. Diz também o que ficou por
 *  resolver: um resumo que só conta o que correu bem deixa quem lê a
 *  pensar que acabou. */
export function resumoDaSincronizacao(r: Resultado): string {
  const partes: string[] = []
  if (r.recebidos > 0) partes.push(`${r.recebidos} recebidos`)
  if (r.enviados > 0) partes.push(`${r.enviados} enviados`)
  if (r.comentariosNovos > 0) {
    partes.push(
      `${r.comentariosNovos} ${r.comentariosNovos === 1 ? 'comentário novo' : 'comentários novos'}`,
    )
  }
  const bloqueados = r.recusados.filter((x) => x.tipo !== 'versao').length
  const alterados = r.recusados.length - bloqueados
  if (bloqueados > 0) {
    partes.push(
      `${bloqueados} ${bloqueados === 1 ? 'bloqueado por outra pessoa' : 'bloqueados por outras pessoas'}`,
    )
  }
  if (alterados > 0) {
    partes.push(
      `${alterados} ${alterados === 1 ? 'alterado entretanto por outra pessoa, a rever na próxima sincronização' : 'alterados entretanto por outras pessoas, a rever na próxima sincronização'}`,
    )
  }
  if (r.conflitos.length > 0) {
    partes.push(
      `${r.conflitos.length} ${r.conflitos.length === 1 ? 'conflito por resolver' : 'conflitos por resolver'}`,
    )
  }
  return partes.length === 0 ? 'Já estava tudo em dia.' : partes.join(', ') + '.'
}
